<template>
    <div class="main-layout__container auction-detail">
        <div class="content__header content__header--one-item-per-line">
            <div class="content-header__back">
                <UIBackButton
                    :text="'К списку аукционов'"
                    :to="router.resolve({ name: 'AuctionsList' }).href"
                />
            </div>

            <div v-if="auction" class="auction-detail__title">
                <div class="content-header__title">
                    <span>Лот №{{ auction.id }}</span>

                    <AuctionItemStatus
                        :status-text="getAuctionStatusLabel(auction.status)"
                        :status-class="auction.status"
                    />
                </div>

                <AuctionItemParticipationStatus
                    v-if="auction.user_joining && !auction.is_winner"
                    status-text="Вы участвуете"
                    status-class="participating"
                />

                <AuctionItemParticipationStatus
                    v-if="auction.is_winner"
                    status-text="Вы победитель!"
                    status-class="winner"
                />

                <UIDropDown
                    v-if="canSendAnnouncementAboutAuction(auction)
                        || canForceCompleteAuction(auction)
                        || canProlongAuction(auction)
                        || canEditAuction(auction)
                        || canAnnulAuction(auction)
                    "
                    as-modal-on-mobile
                >
                    <template #elements>
                        <li
                            v-if="canSendAnnouncementAboutAuction(auction)"
                            class="ui-dropdown-menu__element"
                            @click="openSendAnnouncementAboutAuctionConfirmModal"
                        >
                            <img v-svg-inline src="@/assets/icons/ui/send-announcement.svg" />
                            <span>Отправить анонс</span>
                        </li>

                        <router-link
                            v-if="canEditAuction(auction)"
                            :to="{ name: 'EditAuction', params: { auction_id: auction.id } }"
                            class="ui-dropdown-menu__element"
                        >
                            <img v-svg-inline src="@/assets/icons/ui/edit.svg" />
                            <span>Редактировать</span>
                        </router-link>

                        <li
                            v-if="canProlongAuction(auction)"
                            class="ui-dropdown-menu__element"
                            @click="openProlongAuctionConfirmModal"
                        >
                            <img v-svg-inline src="@/assets/icons/ui/ic_round-more-time.svg" />
                            <span>Продлить</span>
                        </li>

                        <li
                            v-if="canForceCompleteAuction(auction)"
                            class="ui-dropdown-menu__element danger"
                            @click="openForceCompleteAuctionConfirmModal"
                        >
                            <img v-svg-inline src="@/assets/icons/ui/mingcute_close-circle-line.svg" />
                            <span>Завершить</span>
                        </li>

                        <li
                            v-if="canAnnulAuction(auction)"
                            class="ui-dropdown-menu__element"
                            @click="openAnnulAuctionConfirmModal"
                        >
                            <img v-svg-inline src="@/assets/icons/ui/annul.svg" />
                            <span>Аннулировать</span>
                        </li>
                    </template>
                </UIDropDown>
            </div>

            <div class="content-header__background content-header__background--add-edit-page"></div>
        </div>

        <UILoader v-if="isAuctionLoading && !auction" class="detailable" />

        <template v-else-if="!isAuctionLoading && auction">
            <div class="content__body">
                <ViewAuctionInfo
                    :auction="auction"
                    @confirmed-winner="onConfirmedWinner()"
                />

                <div class="auction-detail__content">
                    <div class="auction-detail-car">
                        <div class="auction-detail-car-info">
                            <div class="auction-detail-car-info__title">
                                {{ getCarFullName(auction.car) }}
                            </div>

                            <div v-if="auction.car.tags.length > 0" class="auction-detail-car-info__tags">
                                <CarTags :tags="auction.car.tags" />
                            </div>

                            <div class="auction-detail-car-info__specs">
                                {{ getCarSpecification(auction.car) }}
                            </div>

                            <div v-if="auction.car.comment" class="auction-detail-car-info__description">
                                {{ auction.car.comment }}
                            </div>
                        </div>

                        <AuctionCarGallery :auction="auction" />

                        <div class="auction-detail-car__info-wrapper">
                            <div class="auction-detail-car__info">
                                <CarItemDetails
                                    :details="[
                                        { title: 'Тип поступления:', value: getCarAdmissionTypeLabel(auction.car.admission_type) },
                                        { title: 'Тип НДС:', value: getCarNdsTypeLabel(auction.car.nds_type) },
                                        { title: 'Комплектация:', value: auction.car.complectation.code || '-' },
                                        { title: 'VIN:', value: auction.car.vin },
                                        { title: 'ПТС:', value: auction.car.pts_number },
                                        { title: 'Добавлен:', value: formatFromUTC(auction.car.created_at) },
                                        { title: 'Дилер:', value: getCarDealerFullName(auction.car) },
                                    ]"
                                />

                                <UIAccordionList :items="characteristics">
                                    <template #content-0>
                                        <CarCharacteristics :car="auction.car" />
                                    </template>

                                    <template #content-1>
                                        <CarEquipment :car="auction.car" />
                                    </template>

                                    <template #content-2>
                                        <CarDamage :car="auction.car" />
                                    </template>

                                    <template #content-3>
                                        <CarLegal :car="auction.car" />
                                    </template>

                                    <template #content-4>
                                        <div class="auction-detail-car__info-documents">
                                            <CarDocuments
                                                type-title="Диагностическая карта"
                                                :car-documents="carDocuments.diagnostic_card"
                                                if-no-documents-show-this="file-not-uploaded"
                                            />

                                            <CarDocuments
                                                type-title="Отчет автокод/автотека"
                                                :car-documents="carDocuments.avtocod_report"
                                                if-no-documents-show-this="file-not-uploaded"
                                            />

                                            <CarDocuments
                                                type-title="Дополнительные документы"
                                                :car-documents="carDocuments.document"
                                            />
                                        </div>
                                    </template>
                                </UIAccordionList>
                            </div>

                            <div v-if="canCreateBid(auction) && auction.broadcastInfo">
                                <ViewAuctionMakeBid
                                    :auction-id="auctionId"
                                    :start-price="auction.start_price"
                                    :current-bid="auction.broadcastInfo.last_bid?.value"
                                    :user-bid="
                                        auction.user_bids && auction.user_bids.length > 0
                                            ? auction.user_bids[auction.user_bids.length - 1].value
                                            : undefined
                                    "
                                    @bid="onBid"
                                />

                                <div v-if="auction.delivery_price" class="bid-delivery">
                                    <div class="bid-delivery-block">
                                        <div class="bid-delivery-label">Стоимость доставки</div>
                                        <div class="bid-delivery-value">
                                            {{ numberFormat(auction.delivery_price.price) }} ₽
                                        </div>
                                    </div>

                                    <div class="bid-delivery-block">
                                        <div class="bid-delivery-label">Город доставки</div>
                                        <div class="bid-delivery-value">
                                            {{ auction.delivery_price.city_to_name }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script lang="ts" setup>
    import { computed, h, onMounted, onUnmounted, ref } from 'vue';
    import { useRouter, useRoute } from 'vue-router';

    import { ModalKey } from '@/injection/keys.ts';

    import {
        Auction,
        AuctionStatusBroadcast,
        getAuctionStatusLabel,
        InProgressAuctionBroadcastItem,
    } from '@/types/auction.type.ts';
    import {
        CarDocument,
        CarDocumentType,
        getCarAdmissionTypeLabel,
        getCarNdsTypeLabel,
    } from '@/types/car.type.ts';

    import useAuctionBroadcaster from '@/repositories/auction/useAuctionBroadcaster.ts';
    import useAuctionRepository from '@/repositories/auction/useAuctionRepository.ts';
    import useLoading from '@/utils/useLoading.ts';
    import useNotifications from '@/utils/useNotifications.ts';
    import usePermission from '@/repositories/auth/usePermission.ts';

    import { injectStrict } from '@/injection/injectStrict.ts';
    import { getCarDealerFullName, getCarDocuments, getCarFullName, getCarSpecification } from '@/utils/car';
    import { formatFromUTC } from '@/utils/DateTimeFormatter.ts';
    import { numberFormat } from '@/utils/NumberFormatter.ts';

    import UIAccordionList from '@/components/ui/UIAccordionList/UIAccordionList.vue';
    import UIBackButton from '@/components/ui/UIBackButton/UIBackButton.vue';
    import UIDropDown from '@/components/ui/UIDropDown/UIDropDown.vue';
    import UILoader from '@/components/ui/UILoader/UILoader.vue';

    import AuctionCarGallery from '@/components/auction/AuctionCarGallery/AuctionCarGallery.vue';
    import AuctionItemStatus from '@/components/auction/AuctionItemStatus/AuctionItemStatus.vue';
    import AuctionItemParticipationStatus from '@/components/auction/AuctionItemParticipationStatus/AuctionItemParticipationStatus.vue';
    import CarCharacteristics from '@/components/cars/CarCharacteristics/CarCharacteristics.vue';
    import CarEquipment from '@/components/cars/CarEquipment/CarEquipment.vue';
    import CarDamage from '@/components/cars/CarDamage/CarDamage.vue';
    import CarDocuments from '@/components/cars/CarDocuments/CarDocuments.vue';
    import CarItemDetails from '@/components/cars/CarItemDetails/CarItemDetails.vue';
    import CarLegal from '@/components/cars/CarLegal/CarLegal.vue';
    import CarTags from '@/components/cars/CarTags/CarTags.vue';
    import ContinueAuctionModal from '@/components/auction/ContinueAuctionModal/ContinueAuctionModal.vue';
    import AuctionConfirmSendAnnouncement from '@/components/auction/AuctionConfirmSendAnnouncement/AuctionConfirmSendAnnouncement.vue';
    import AuctionConfirmAnnul from '@/components/auction/AuctionConfirmAnnul/AuctionConfirmAnnul.vue';

    import ConfirmActionModal from '@/components/modals/ConfirmActionModal/ConfirmActionModal.vue';

    import ViewAuctionInfo from '@/views/auctions/ViewAuction/components/ViewAuctionInfo/ViewAuctionInfo.vue';
    import ViewAuctionMakeBid from '@/views/auctions/ViewAuction/components/ViewAuctionMakeBid/ViewAuctionMakeBid.vue';

    const $modal = injectStrict(ModalKey);

    const router = useRouter();
    const route = useRoute();
    const {
        getAuction,
        prolongAuction,
        forceCompleteAuction,
        sendAnnouncementAboutAuction,
        annulAuction,
    } = useAuctionRepository();
    const { isLoading: isAuctionLoading, setLoading: setAuctionLoading } = useLoading();
    const {
        listenInProgressAuctions,
        leaveInProgressAuctions,
        listenAuctionStatus,
        leaveAuctionStatus,
    } = useAuctionBroadcaster();
    const {
        canCreateBid,
        canForceCompleteAuction,
        canProlongAuction,
        canEditAuction,
        canSendAnnouncementAboutAuction,
        canAnnulAuction,
    } = usePermission();
    const { showSuccess } = useNotifications();

    const auction = ref<Auction | null>(null);
    const characteristics = ref([
        { title: 'Характеристики', content: '' },
        { title: 'Комплектация', content: '' },
        { title: 'Диагностика и повреждения', content: '' },
        { title: 'Юридическая чистота', content: '' },
        { title: 'Документы', content: '' },
    ]);

    const carDocuments = computed<Record<CarDocumentType, CarDocument[]>>(() => {
        return getCarDocuments(auction.value?.car);
    });

    const auctionId = parseInt(route.params.auction_id as string);

    function onBid(): void {
        fetchAuction();
    }

    function onConfirmedWinner(): void {
        showSuccess({
            title: 'Победитель аукциона подтвержден',
        });

        fetchAuction();
    }

    function openSendAnnouncementAboutAuctionConfirmModal(): void {
        $modal?.open({
            component: ConfirmActionModal,
            modalType: 'narrow',
            slots: {
                default: () =>
                    auction.value ? h(AuctionConfirmSendAnnouncement, { auction: auction.value }) : '',
            },
            props: {
                onConfirm: async () => {
                    await sendAnnouncementAboutAuction(auctionId);

                    showSuccess({
                        title: 'Анонс отправлен',
                    });
                },
            },
        });
    }

    function openAnnulAuctionConfirmModal(): void {
        $modal?.open({
            component: ConfirmActionModal,
            modalType: 'narrow',
            slots: {
                default: () =>
                    auction.value ? h(AuctionConfirmAnnul, { auction: auction.value }) : '',
            },
            props: {
                onConfirm: async () => {
                    await annulAuction(auctionId);

                    showSuccess({
                        title: 'Аукцион аннулирован',
                    });

                    fetchAuction();
                },
            },
        });
    }

    function openProlongAuctionConfirmModal(): void {
        $modal?.open({
            component: ContinueAuctionModal,
            props: {
                auction,
                onConfirmed: onProlongAuction,
            },
            modalType: 'narrow',
        });
    }

    function onProlongAuction(minutes: number): void {
        $modal?.close();
        prolongAuction(auctionId, minutes).then(() => {
            showSuccess({
                title: 'Аукцион продлен',
            });

            fetchAuction();
        });
    }

    function openForceCompleteAuctionConfirmModal(): void {
        onForceCompleteAuction();
    }

    function onForceCompleteAuction(): void {
        forceCompleteAuction(auctionId).then(() => {
            showSuccess({
                title: 'Аукцион завершен',
            });

            fetchAuction();
        });
    }

    function onInProgressAuctionsBroadcast(auctions: InProgressAuctionBroadcastItem[]): void {
        if (auction.value !== null) {
            auction.value.broadcastInfo = auctions.find(
                (auctionInfo: InProgressAuctionBroadcastItem) => auctionInfo.lot_id === auctionId,
            );
        }
    }

    function onAuctionStatusBroadcast(auctionInfo: AuctionStatusBroadcast): void {
        if (auctionInfo.lot_id === auctionId) {
            fetchAuction();
        }
    }

    function fetchAuction(): void {
        setAuctionLoading(true);

        getAuction(auctionId)
            .then((fetchedAuction: Auction) => {
                auction.value = fetchedAuction;
                setAuctionLoading(false);
            });
    }

    onMounted(() => {
        fetchAuction();
        listenInProgressAuctions(onInProgressAuctionsBroadcast);
        listenAuctionStatus(onAuctionStatusBroadcast);
    });

    onUnmounted(() => {
        leaveInProgressAuctions();
        leaveAuctionStatus();
    });
</script>

<style scoped lang="scss">
    @use './ViewAuction.scss' as *;
</style>
